import React, { useState, useEffect } from 'react';
import { rulesetsApi, banlistsApi, formatsApi, cardsApi } from '../services/api';
import { Ruleset, RulesetCreate, RulesetUpdate, BannedCard, Format, Card } from '../types';
import CardSearchOverlay from '../components/CardSearchOverlay';
import BanlistDisplay from '../components/BanlistDisplay';

const RulesetsPage: React.FC = () => {
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedRuleset, setSelectedRuleset] = useState<Ruleset | null>(null);

  useEffect(() => {
    loadRulesets();
  }, []);

  const loadRulesets = async () => {
    try {
      setLoading(true);
      const response = await rulesetsApi.getAll();
      setRulesets(response.data);
    } catch (error) {
      console.error('Failed to load rulesets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRuleset = async (rulesetData: RulesetCreate) => {
    try {
      const response = await rulesetsApi.create(rulesetData);
      setRulesets([response.data, ...rulesets]);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create ruleset:', error);
      alert('Failed to create ruleset. Please try again.');
    }
  };

  const handleDeleteRuleset = async (rulesetId: string) => {
    if (!window.confirm('Are you sure you want to delete this ruleset? This action cannot be undone.')) {
      return;
    }

    try {
      await rulesetsApi.delete(rulesetId);
      setRulesets(rulesets.filter(r => r.id !== rulesetId));
      if (selectedRuleset?.id === rulesetId) {
        setSelectedRuleset(null);
      }
    } catch (error: any) {
      console.error('Failed to delete ruleset:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete ruleset. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateRuleset = async (rulesetId: string, rulesetData: RulesetUpdate) => {
    try {
      const response = await rulesetsApi.update(rulesetId, rulesetData);
      setRulesets(rulesets.map(r => r.id === rulesetId ? response.data : r));
      if (selectedRuleset?.id === rulesetId) {
        setSelectedRuleset(response.data);
      }
    } catch (error: any) {
      console.error('Failed to update ruleset:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update ruleset. Please try again.';
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading rulesets...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Rulesets</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Ruleset
          </button>
        </div>

        {showCreateForm && (
          <CreateRulesetForm
            onSubmit={handleCreateRuleset}
            onCancel={() => setShowCreateForm(false)}
            onRulesetCreated={(ruleset) => {
              setRulesets([ruleset, ...rulesets]);
              setShowCreateForm(false);
            }}
          />
        )}

        {selectedRuleset && (
          <RulesetDetails
            ruleset={selectedRuleset}
            onClose={() => setSelectedRuleset(null)}
            onUpdate={handleUpdateRuleset}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rulesets.map((ruleset) => (
            <div
              key={ruleset.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer relative"
              onClick={() => setSelectedRuleset(ruleset)}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRuleset(ruleset.id);
                }}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                title="Delete ruleset"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 pr-8">{ruleset.name}</h3>
              <p className="text-gray-600 mb-2">{ruleset.description}</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Budget:</span>
                  <span className="text-sm font-medium">
                    {ruleset.budget_limit 
                      ? `${ruleset.budget_currency === 'EUR' ? '€' : ruleset.budget_currency === 'USD' ? '$' : 'TIX'}${ruleset.budget_limit}`
                      : 'No Budget'
                    }
                  </span>
                </div>
              
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Format:</span>
                  <span className="text-sm font-medium">{ruleset.format}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {rulesets.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No rulesets found. Create your first ruleset!</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface CreateRulesetFormProps {
  onSubmit: (ruleset: RulesetCreate) => void;
  onCancel: () => void;
  onRulesetCreated: (ruleset: Ruleset) => void;
}

const CreateRulesetForm: React.FC<CreateRulesetFormProps> = ({ onSubmit, onCancel, onRulesetCreated }) => {
  const [formData, setFormData] = useState<RulesetCreate>({
    name: '',
    description: '',
    format: 'commander',
    budget_limit: null,
    budget_currency: 'EUR',
    
    deck_size_min: 100,
    deck_size_max: 100,
    basic_lands_free: true,
  });
  
  const [hasBudgetLimit, setHasBudgetLimit] = useState(false);
  const [customFormat, setCustomFormat] = useState(false);
  const [availableFormats, setAvailableFormats] = useState<Format[]>([]);
  const [loadingFormats, setLoadingFormats] = useState(true);
  
  // Banlist management state
  const [showBanlistSection, setShowBanlistSection] = useState(false);
  const [banlistCards, setBanlistCards] = useState<BannedCard[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Card[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadFormats();
  }, []);

  const loadFormats = async () => {
    try {
      setLoadingFormats(true);
      const response = await formatsApi.getAll();
      setAvailableFormats(response.data.formats);
    } catch (error) {
      console.error('Failed to load formats:', error);
    } finally {
      setLoadingFormats(false);
    }
  };

  const handleSearchCards = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await cardsApi.search(searchQuery);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddToBanlist = (card: Card) => {
    const newBannedCard: BannedCard = {
      id: `temp-${Date.now()}`,
      banlist_id: '',
      oracle_id: card.oracle_id,
      card_name: card.name,
      reason: '',
      created_at: new Date().toISOString(),
    };
    setBanlistCards([...banlistCards, newBannedCard]);
  };

  const handleRemoveFromBanlist = (cardId: string) => {
    setBanlistCards(banlistCards.filter(card => card.id !== cardId));
  };

  const isCardInBanlist = (oracleId: string) => {
    return banlistCards.some(card => card.oracle_id === oracleId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required');
      return;
    }
    
    // Set budget to null if checkbox is unchecked
    const submitData = {
      ...formData,
      budget_limit: hasBudgetLimit ? formData.budget_limit : null,
    };
    
    try {
      // Create the ruleset first
      const response = await rulesetsApi.create(submitData);
      const createdRuleset = response.data;
      
      // If there are banlist cards, add them to the ruleset
      if (banlistCards.length > 0) {
        for (const bannedCard of banlistCards) {
          try {
            await banlistsApi.addCard(createdRuleset.id, {
              oracle_id: bannedCard.oracle_id,
              card_name: bannedCard.card_name,
              reason: bannedCard.reason || '',
            });
          } catch (error) {
            console.error('Failed to add card to banlist:', error);
            // Continue with other cards even if one fails
          }
        }
      }
      
      // Update parent state with the created ruleset (don't call onSubmit to avoid duplicate creation)
      onRulesetCreated(createdRuleset);
    } catch (error) {
      console.error('Failed to create ruleset with banlist:', error);
      alert('Failed to create ruleset. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Create Ruleset</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Budget Commander"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format *
            </label>
            <select
              value={customFormat ? 'custom' : (formData.format || 'commander')}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setCustomFormat(true);
                  setFormData({ ...formData, format: '' });
                } else {
                  setCustomFormat(false);
                  setFormData({ ...formData, format: e.target.value });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loadingFormats}
            >
              {loadingFormats ? (
                <option>Loading formats...</option>
              ) : (
                <>
                  {availableFormats.map((format) => (
                    <option key={format.name} value={format.name}>
                      {format.display_name}
                    </option>
                  ))}
                  <option value="custom">Custom Format</option>
                </>
              )}
            </select>
            {customFormat && (
              <input
                type="text"
                value={formData.format || ''}
                onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                placeholder="Enter custom format name"
              />
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Optional description of the ruleset..."
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="has_budget_limit"
              checked={hasBudgetLimit}
              onChange={(e) => {
                setHasBudgetLimit(e.target.checked);
                if (!e.target.checked) {
                  setFormData({ ...formData, budget_limit: null });
                } else {
                  setFormData({ ...formData, budget_limit: 50 });
                }
              }}
              className="mr-2"
            />
            <label htmlFor="has_budget_limit" className="text-sm font-medium text-gray-700">
              Has Budget Limit
            </label>
          </div>
          
          {hasBudgetLimit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Limit *
                </label>
                <input
                  type="number"
                  value={formData.budget_limit || ''}
                  onChange={(e) => setFormData({ ...formData, budget_limit: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency *
                </label>
                <select
                  value={formData.budget_currency}
                  onChange={(e) => setFormData({ ...formData, budget_currency: e.target.value as 'EUR' | 'USD' | 'TIX' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="TIX">TIX</option>
                </select>
              </div>
            </div>
          )}
        </div>

        

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="basic_lands_free"
              checked={formData.basic_lands_free}
              onChange={(e) => setFormData({ ...formData, basic_lands_free: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="basic_lands_free" className="text-sm font-medium text-gray-700">
              Treat basic lands as free
            </label>
          </div>
        </div>

        {/* Banlist Section */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Banlist (Optional)</h3>
            <button
              type="button"
              onClick={() => setShowBanlistSection(!showBanlistSection)}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              {showBanlistSection ? 'Hide' : 'Manage Banlist'}
            </button>
          </div>

          {showBanlistSection && (
            <div className="space-y-4">
              {/* Card Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Cards to Ban
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchCards()}
                    placeholder="Enter card name..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleSearchCards}
                    disabled={searchLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                  <div className="p-2 space-y-1">
                    {searchResults.map((card) => (
                      <div
                        key={card.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div className="flex items-center space-x-2">
                          {card.image_uri && (
                            <img
                              src={card.image_uri}
                              alt={card.name}
                              className="w-8 h-10 object-cover rounded"
                            />
                          )}
                          <span className="text-sm font-medium">{card.name}</span>
                          <span className="text-xs text-gray-500">({card.set_code})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddToBanlist(card)}
                          disabled={isCardInBanlist(card.oracle_id)}
                          className={`px-2 py-1 rounded text-xs ${
                            isCardInBanlist(card.oracle_id)
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700'
                          }`}
                        >
                          {isCardInBanlist(card.oracle_id) ? 'Added' : 'Ban'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Banlist */}
              {banlistCards.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banned Cards ({banlistCards.length})
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    <div className="p-2 space-y-1">
                      {banlistCards.map((bannedCard) => (
                        <div
                          key={bannedCard.id}
                          className="flex items-center justify-between p-2 bg-red-50 rounded"
                        >
                          <span className="text-sm font-medium">{bannedCard.card_name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromBanlist(bannedCard.id)}
                            className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Ruleset
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

interface RulesetDetailsProps {
  ruleset: Ruleset;
  onClose: () => void;
  onUpdate: (rulesetId: string, rulesetData: RulesetUpdate) => void;
}

const RulesetDetails: React.FC<RulesetDetailsProps> = ({ ruleset, onClose, onUpdate }) => {
  const [showBanlistOverlay, setShowBanlistOverlay] = useState(false);
  const [bannedCards, setBannedCards] = useState<BannedCard[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<RulesetUpdate>({});

  useEffect(() => {
    loadBannedCards();
  }, [ruleset.id]);

  const loadBannedCards = async () => {
    try {
      const response = await banlistsApi.getAll(ruleset.id);
      setBannedCards(response.data);
    } catch (error) {
      console.error('Failed to load banned cards:', error);
    }
  };

  const handleBannedCardsChange = (newBannedCards: BannedCard[]) => {
    setBannedCards(newBannedCards);
  };

  const handleCloseBanlistOverlay = () => {
    setShowBanlistOverlay(false);
    // Trigger refresh of BanlistDisplay component
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditFormData({
      name: ruleset.name,
      description: ruleset.description || '',
      format: ruleset.format,
      budget_limit: ruleset.budget_limit,
      budget_currency: ruleset.budget_currency,
      deck_size_min: ruleset.deck_size_min,
      deck_size_max: ruleset.deck_size_max,
      basic_lands_free: ruleset.basic_lands_free,
    });
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  const handleEditSubmit = () => {
    onUpdate(ruleset.id, editFormData);
    setIsEditing(false);
    setEditFormData({});
  };

  const handleEditFormChange = (field: keyof RulesetUpdate, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{ruleset.name}</h2>
          <div className="flex space-x-2">
            {!isEditing && (
              <button
                onClick={handleEditClick}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editFormData.description || ''}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Budget Limit</label>
                <input
                  type="number"
                  step="0.01"
                  value={editFormData.budget_limit || ''}
                  onChange={(e) => handleEditFormChange('budget_limit', e.target.value ? parseFloat(e.target.value) : null)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Budget Currency</label>
                <select
                  value={editFormData.budget_currency || 'EUR'}
                  onChange={(e) => handleEditFormChange('budget_currency', e.target.value as 'EUR' | 'USD' | 'TIX')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="TIX">TIX</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Format</label>
                <input
                  type="text"
                  value={editFormData.format || ''}
                  onChange={(e) => handleEditFormChange('format', e.target.value || null)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Deck Size Min</label>
                <input
                  type="number"
                  value={editFormData.deck_size_min || ''}
                  onChange={(e) => handleEditFormChange('deck_size_min', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Deck Size Max</label>
                <input
                  type="number"
                  value={editFormData.deck_size_max || ''}
                  onChange={(e) => handleEditFormChange('deck_size_max', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editFormData.basic_lands_free || false}
                    onChange={(e) => handleEditFormChange('basic_lands_free', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Basic Lands Free</span>
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ruleset.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="text-gray-900">{ruleset.description}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Budget Limit</label>
                <p className="text-gray-900">
                  {ruleset.budget_limit 
                    ? `${ruleset.budget_currency === 'EUR' ? '€' : ruleset.budget_currency === 'USD' ? '$' : 'TIX'}${ruleset.budget_limit}`
                    : 'No Budget'
                  }
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Format</label>
                <p className="text-gray-900 capitalize">{ruleset.format}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Basic Lands Free</label>
                <p className="text-gray-900">{ruleset.basic_lands_free ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="text-gray-900">{new Date(ruleset.created_at).toLocaleString()}</p>
              </div>
            </div>

            {/* Banlist Section */}
            <div>
              <BanlistDisplay
                rulesetId={ruleset.id}
                onManageBanlist={() => setShowBanlistOverlay(true)}
                refreshTrigger={refreshTrigger}
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Banlist Overlay */}
      <CardSearchOverlay
        isOpen={showBanlistOverlay}
        onClose={handleCloseBanlistOverlay}
        rulesetId={ruleset.id}
        onBannedCardsChange={handleBannedCardsChange}
        existingBannedCards={bannedCards}
      />
    </div>
  );
};

export default RulesetsPage;
